import React, { useState } from "react";
import { Box, useMediaQuery, useTheme, CircularProgress, Typography, Alert, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';

// Create a custom hook to export the toggle functionality
export const useRatingChartToggle = () => {
  const [viewMode, setViewMode] = useState('cumulative');
  
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  return {
    viewMode,
    handleViewModeChange
  };
};

// Create a component for the toggle buttons
export const RatingChartToggle = ({ viewMode, handleViewModeChange, size = 'small' }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <ToggleButtonGroup
      value={viewMode}
      exclusive
      onChange={handleViewModeChange}
      size={size}
      sx={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
        border: '1px solid rgba(25, 118, 210, 0.1)',
        borderRadius: '12px !important',
        boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
        height: size === 'small' ? '32px' : '36px',
        '&:hover': {
          boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
          backgroundColor: 'rgba(25, 118, 210, 0.04)'
        },
        '& .MuiToggleButton-root': {
          border: 'none',
          fontSize: size === 'small' ? '0.8rem' : '0.9rem',
          fontWeight: 'bold',
          minWidth: isSmallScreen ? '65px' : '80px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            color: '#1976d2'
          },
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.04)'
          },
          '&:not(:last-child)': {
            borderRight: '1px solid rgba(25, 118, 210, 0.2)'
          }
        }
      }}
    >
      <Tooltip title="Show cumulative rating over time" arrow>
        <ToggleButton 
          value="cumulative" 
          aria-label="cumulative rating"
          sx={{
            color: '#546E7A',
            fontWeight: 'bold',
            textTransform: 'capitalize',
            '&.Mui-selected': {
              color: '#1976d2',
            }
          }}
        >
          Cumulative
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Show monthly average ratings" arrow>
        <ToggleButton 
          value="monthly" 
          aria-label="monthly average rating"
          sx={{
            color: '#546E7A',
            fontWeight: 'bold',
            textTransform: 'capitalize',
            '&.Mui-selected': {
              color: '#1976d2',
            }
          }}
        >
          Monthly
        </ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  );
};

// Shared utility functions
export const processChartData = (reviews) => {
  if (!reviews || reviews.length === 0) {
    return { chartData: null, monthlyData: null };
  }

  try {
    // Process cumulative rating data
    const xData = reviews.map(review => new Date(review.estimated_datetime));
    const yData = reviews.map(review => review.cumulative_rating);
    
    // Handle possible empty or invalid data
    if (xData.length === 0 || yData.length === 0 || 
        yData.some(val => val === undefined || val === null || isNaN(val))) {
      console.warn("Invalid data found in reviews for chart");
      return { chartData: null, monthlyData: null };
    }

    // Calculate min and max values with a small buffer for cumulative chart
    const minValue = Math.min(...yData);
    const maxValue = Math.max(...yData);
    const range = maxValue - minValue || 1; // Prevent division by zero
    const buffer = range * 0.1; // 10% buffer

    const chartData = {
      xData,
      yData,
      yMin: Math.max(0, minValue - buffer), // Floor at 0
      yMax: Math.min(5, maxValue + buffer)  // Ceiling at 5 (max rating)
    };

    // Process monthly average ratings for bar chart
    const monthlyRatings = {};
    
    // Group reviews by month
    reviews.forEach(review => {
      const date = new Date(review.estimated_datetime);
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
      
      if (!monthlyRatings[monthYear]) {
        monthlyRatings[monthYear] = { sum: 0, count: 0 };
      }
      
      monthlyRatings[monthYear].sum += review.rating;
      monthlyRatings[monthYear].count += 1;
    });
    
    // Calculate averages
    const months = Object.keys(monthlyRatings);
    const averages = months.map(month => {
      const { sum, count } = monthlyRatings[month];
      return count > 0 ? sum / count : 0;
    });
    
    // Calculate min and max values with buffer for monthly data
    const monthlyMinValue = Math.min(...averages);
    const monthlyMaxValue = Math.max(...averages);
    const monthlyRange = monthlyMaxValue - monthlyMinValue || 1; // Prevent division by zero
    const monthlyBuffer = monthlyRange * 0.15; // 15% buffer for monthly chart
    
    const monthlyData = {
      months,
      averages,
      yMin: Math.max(0, monthlyMinValue - monthlyBuffer), // Floor at 0
      yMax: Math.min(5, monthlyMaxValue + monthlyBuffer)  // Ceiling at 5 (max rating)
    };

    return { chartData, monthlyData };
  } catch (error) {
    console.error("Error processing review data for chart:", error);
    return { chartData: null, monthlyData: null };
  }
};

// Shared loading and error states
export const ChartStateWrapper = ({ loading, error, noData, children }) => {
  const containerStyle = {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 0,
    minHeight: { xs: '250px', md: 'auto' },
    maxHeight: { xs: '350px', md: 'none' },
    width: '100%',
    position: 'relative',
    overflow: 'hidden'
  };

  if (loading) {
    return (
      <Box sx={containerStyle}>
        <CircularProgress size={40} color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={containerStyle}>
        <Alert 
          severity="error" 
          sx={{ 
            width: 'calc(100% - 32px)', 
            maxWidth: '400px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (noData) {
    return (
      <Box sx={containerStyle}>
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            color: '#546E7A',
            padding: 2,
            width: '100%'
          }}
        >
          No rating data available for the selected time period
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100%', 
      p: 0,
      display: 'flex',
      flexDirection: 'column',
      minHeight: { xs: '250px', md: 'auto' },
      width: '100%',
      maxHeight: { xs: '350px', md: 'none' },
      position: 'relative',
      overflow: 'hidden'
    }}>
      {children}
    </Box>
  );
}; 